/**
 * 检查项配置页面
 * 左侧：手机模拟器（选择样本 → 调用 apiSimulatePhoto）
 * 右侧：活跃点位追踪面板（每 3 秒轮询 apiGetActiveCheckpoints）
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Button, Modal, Tag } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
  CloudUploadOutlined,
  FormOutlined,
  IdcardOutlined,
  MenuOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  RedoOutlined,
  RobotOutlined,
  ScanOutlined,
  ThunderboltOutlined,
  VideoCameraOutlined
} from '@ant-design/icons'
import { useAppSelector, useAppDispatch } from '../../../app/hooks'
import { TrackerCheckpoint, SimulateResult } from '../constants'
import { apiSimulatePhoto, apiGetActiveCheckpoints } from '../api'
import defaultBgImg from './default-bg.png'
import syncDoneImg from './sync-done.png'
import './index.scss'

// ============================================================
// 阶段标签
// ============================================================

const STAGE_LABELS: Record<string, string> = {
  collecting: '收集中',
  analyzing: 'AI分析中',
  compliant: '已通过',
  non_compliant: '不符合',
  rectifying: '整改中',
  reviewing: '审核中',
  rejected: '已驳回',
  appeal_reviewing: '复议中',
  approved: '已完成'
}

// ============================================================
// Pipeline 辅助
// ============================================================

type NodeState = 'complete' | 'active' | 'fail' | 'waiting'

interface PipelineNodeDef {
  label: string
  state: NodeState
}

/** 根据 tracker 计算每个 pipeline 节点状态（对齐原型逻辑） */
function computeNodeStates(tracker: TrackerCheckpoint): PipelineNodeDef[] {
  const s = tracker.stage
  const isNonCompliant = [
    'non_compliant',
    'rectifying',
    'reviewing',
    'rejected',
    'appeal_reviewing',
    'approved'
  ].includes(s)

  if (isNonCompliant) {
    // 6 节点：收集 → 分析 → 结果(fail) → 工单 → 整改 → 完成
    const nodes: PipelineNodeDef[] = [
      { label: '收集', state: 'complete' },
      { label: '分析', state: 'complete' },
      { label: '结果', state: 'fail' },
      { label: '工单', state: 'complete' },
      { label: '整改', state: 'waiting' },
      { label: '完成', state: 'waiting' }
    ]
    if (s === 'non_compliant') {
      nodes[3].state = 'waiting'
    } else if (
      ['rectifying', 'rejected', 'reviewing', 'appeal_reviewing'].includes(s)
    ) {
      nodes[4].state = 'active'
    } else if (s === 'approved') {
      nodes[4].state = 'complete'
      nodes[5].state = 'complete'
    }
    return nodes
  }

  // 4 节点：收集 → 分析 → 结果 → 通过
  const nodes: PipelineNodeDef[] = [
    { label: '收集', state: 'waiting' },
    { label: '分析', state: 'waiting' },
    { label: '结果', state: 'waiting' },
    { label: '通过', state: 'waiting' }
  ]
  if (s === 'collecting') {
    nodes[0].state = 'active'
  } else if (s === 'analyzing') {
    nodes[0].state = 'complete'
    nodes[1].state = 'active'
  } else if (s === 'compliant') {
    nodes[0].state = 'complete'
    nodes[1].state = 'complete'
    nodes[2].state = 'complete'
    nodes[3].state = 'complete'
  }
  return nodes
}

/** 两节点之间的连线状态 */
function getLineState(nodes: PipelineNodeDef[], idx: number): string {
  const cur = nodes[idx]
  const next = nodes[idx + 1]
  const curDone = cur.state === 'complete' || cur.state === 'fail'
  const nextDone = next.state === 'complete' || next.state === 'fail'
  if (curDone && nextDone) return 'complete'
  if (curDone && next.state === 'active') return 'flowing'
  return 'waiting'
}

/** 格式化时间为 HH:MM */
function shortTime(t: string): string {
  if (!t) return ''
  const m = t.match(/(\d{2}:\d{2}):\d{2}/)
  return m ? m[1] : t.slice(-5)
}

// ============================================================
// 样本数据
// ============================================================

interface SampleMedia {
  url: string
  type: 'image' | 'video'
  desc?: string
}

interface SampleItem {
  code: string
  label: string
  tag: '正样本' | '负样本'
  desc?: string
  media: SampleMedia[]
  rectifyMedia?: SampleMedia[] // 整改后媒体
}

interface SampleCategory {
  id: string
  name: string
  standard?: string
  items: SampleItem[]
}

const P = 'https://net-cloud.xhey.top/group/photo/'
const DEFAULT_HOME_PREVIEW =
  'https://net-cloud.xhey.top/group/photo/c9616932-47ae-42a5-8b5b-88e3c1bb48aa.jpg'

const SAMPLE_DATA: SampleCategory[] = [
  {
    id: 'bolt-stretch',
    name: '1. 轮毂变桨轴承螺柱拉伸',
    items: [
      {
        code: 'AG01',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + 'c9616932-47ae-42a5-8b5b-88e3c1bb48aa.jpg',
            type: 'image'
          },
          {
            url: P + 'c9b992c6-657f-4b99-ba73-b3f113643863.jpg',
            type: 'image'
          },
          { url: P + 'c989b2eb-e979-4e01-bd60-858c82dc222a.mp4', type: 'video' }
        ]
      },
      {
        code: 'TF01',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '102dcea6-89e9-426c-9cba-78009ca8c7c0.jpg',
            type: 'image'
          },
          {
            url: P + 'f2a03d07-ed53-4613-ae2e-f7edd86eaa6d.jpg',
            type: 'image'
          },
          { url: P + 'f9ca355a-0020-49da-82ae-c2bcbea7fc94.mp4', type: 'video' }
        ]
      },
      {
        code: 'YE15',
        label: '负样本',
        tag: '负样本',
        desc: '视频正确，单视频压力值与图像换算压力值不等',
        media: [
          {
            url: P + 'f5e55d30-73ca-4e52-a81b-816bb59c5bdd.jpg',
            type: 'image'
          },
          {
            url: P + 'fda35929-3a84-4247-a3d8-a02a76573a5b.jpg',
            type: 'image'
          },
          { url: P + 'b2651956-e59e-455e-8826-66106e83890a.mp4', type: 'video' }
        ]
      },
      {
        code: 'NX01',
        label: '负样本',
        tag: '负样本',
        desc: '图像正确，但视频存在多种问题',
        media: [
          {
            url: P + 'b0e4541d-351a-4dc4-b399-96844f24e751.jpg',
            type: 'image'
          },
          {
            url: P + 'ca8e83be-6780-4b8a-880f-f51d9774e61e.jpg',
            type: 'image'
          },
          {
            url: P + '80d439df-78a0-4f5c-a03c-bc2ad93bb6d8.mp4',
            type: 'video',
            desc: '实际校准拉伸6颗，但视频中不到6颗，缺失6个防松线确认'
          },
          {
            url: P + '3581f961-2ce2-4255-98f6-68a5c99a4433.mp4',
            type: 'video',
            desc: '校准4颗螺栓4颗防松线，缺失2个'
          },
          {
            url: P + '78b11376-145a-42a5-91e9-edc4b6d886ce.mp4',
            type: 'video',
            desc: '实际只拍摄6颗校准螺栓，防松线有2个错位'
          },
          {
            url: P + '78cfdf0a-200d-40aa-8547-e04edc482aea.mp4',
            type: 'video',
            desc: '人员遮挡2颗、未拍摄防松线'
          },
          {
            url: P + '7005ab8b-1fb7-4a8b-ac29-acf5f74f7973.mp4',
            type: 'video',
            desc: '只有4颗螺栓校准过程，缺失两个'
          }
        ]
      }
    ]
  },
  {
    id: 'rear-ring-gap',
    name: '2. 后挡圈与主轴承间隙(M3)',
    standard:
      '工艺标准：0.08mm塞尺塞不进，要求水印有4张图像（3点/6点/9点/12点方向各一张）',
    items: [
      {
        code: 'AT10',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '47a75e20-7c35-4a33-a362-a83054eb7ed8.jpg',
            type: 'image'
          },
          {
            url: P + 'c7cc158e-6b0d-497f-a179-2cf210a6715c.jpg',
            type: 'image'
          },
          {
            url: P + '921707e7-69a2-4a1d-a14f-34a9c94fa618.jpg',
            type: 'image'
          },
          { url: P + 'd476ae67-2aa5-4935-a00f-08a5f3923a80.jpg', type: 'image' }
        ]
      },
      {
        code: 'LZ05',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '90792eb9-ad06-45a5-a8a8-b4a5cedd148c.jpg',
            type: 'image'
          },
          {
            url: P + 'ec471afc-e1cd-4e28-8c31-c0e908c1f67b.jpg',
            type: 'image'
          },
          {
            url: P + '7b0f0968-42b4-408b-9e84-91dd74993cc8.jpg',
            type: 'image'
          },
          { url: P + '9209a608-6468-4652-98d7-b6cd4acb4263.jpg', type: 'image' }
        ]
      },
      {
        code: 'AT09',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '676a6c07-5aab-4dfb-85fa-3cb9902e75c9.jpg',
            type: 'image'
          },
          {
            url: P + '91082aea-80ab-4ad9-908b-eab291d6a5b0.jpg',
            type: 'image'
          },
          {
            url: P + '7c1070ce-4614-4c09-aca5-4bfc563a73d9.jpg',
            type: 'image'
          },
          { url: P + '76439716-240c-409d-901f-522ba8519da1.jpg', type: 'image' }
        ]
      },
      {
        code: 'AT08',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + 'dfa90726-e367-464e-9cd9-20e1ebd79668.jpg',
            type: 'image'
          },
          {
            url: P + '772819bf-0a12-4c94-bdf3-e05f0831e75b.jpg',
            type: 'image'
          },
          {
            url: P + 'e7071f2b-0ec3-4fe0-a220-3f33cfbb19dd.jpg',
            type: 'image'
          },
          { url: P + 'd7c0fc4c-41e4-43b8-8738-ee130f4dd66d.jpg', type: 'image' }
        ]
      },
      {
        code: 'AT07',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '578ff29f-665b-4154-b6d3-dde28b842178.jpg',
            type: 'image'
          },
          {
            url: P + 'b7bc19f7-e51f-46ef-afe2-2269f0322a19.jpg',
            type: 'image'
          },
          {
            url: P + 'bd511505-9d23-43cd-96c1-ce81f3f156b4.jpg',
            type: 'image'
          },
          { url: P + '969226e3-9723-42b5-b1dd-462dcdd80b37.jpg', type: 'image' }
        ]
      },
      {
        code: 'AG12',
        label: '负样本',
        tag: '负样本',
        desc: '水印正确，但不符合工艺标准',
        media: [
          {
            url: P + 'c3484622-4454-4879-bbfe-00117adce20a.jpg',
            type: 'image'
          },
          {
            url: P + 'bf4bb351-87a3-4117-af10-097976dbbf27.jpg',
            type: 'image'
          },
          {
            url: P + '862067c0-fe49-4a73-b331-bc0628c8a6f8.jpg',
            type: 'image'
          },
          { url: P + '65b1b242-0b55-4838-8eb2-53e509707337.jpg', type: 'image' }
        ],
        rectifyMedia: [
          {
            url: P + 'd8bc02a0-4682-49c4-adc0-631769bb9435.jpg',
            type: 'image'
          },
          {
            url: P + '72d2e63e-d2b7-4567-a515-9be1e7e28de7.jpg',
            type: 'image'
          },
          {
            url: P + '006b2409-a779-4bc1-bd3e-081e1ef1f1de.jpg',
            type: 'image'
          },
          { url: P + 'e9b3eca1-a2fb-42ab-bd91-d3316deb5ca3.jpg', type: 'image' }
        ]
      }
    ]
  },
  {
    id: 'gearbox-shrink',
    name: '3. 齿轮箱收缩盘与行星转架端面(M3)',
    standard:
      '工艺标准：电子读数10±2mm，每个读数均包含两位小数(mm)，所有读数最大差值≤0.5mm，另需一张铭牌数据（厂家、型号、出厂编号）',
    items: [
      {
        code: 'XQ04',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '0175b287-75c3-45fc-84a4-60c7044e5606.jpg',
            type: 'image'
          },
          {
            url: P + 'd7247ee8-89ad-406e-89f0-10d896606ca2.jpg',
            type: 'image'
          },
          {
            url: P + '6ace1f18-c351-47bb-a55e-5bcc4b548f96.jpg',
            type: 'image'
          },
          {
            url: P + '35c4f061-96db-49df-8064-0bee0dbeb9aa.jpg',
            type: 'image'
          },
          { url: P + '3e3baa49-5f75-4573-9b88-ffb3c85087e5.jpg', type: 'image' }
        ]
      },
      {
        code: 'AT07',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '43f3ab2b-e4fb-4383-b02e-93538b1db464.jpg',
            type: 'image'
          },
          {
            url: P + 'b6c19465-1e89-4382-855a-29bfbf95bb63.jpg',
            type: 'image'
          },
          {
            url: P + '0b162c74-44ff-4e9d-a16b-f8345f607997.jpg',
            type: 'image'
          },
          {
            url: P + '9fb1046e-f34f-49f3-944a-d383a74baa36.jpg',
            type: 'image'
          },
          { url: P + 'a98be328-5083-41ef-98d0-9bacf666569b.jpg', type: 'image' }
        ]
      },
      {
        code: 'AG10',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '33269440-24a3-4ca0-8d6b-6c5f4fd7503a.jpg',
            type: 'image'
          },
          {
            url: P + '2deeec77-7bb0-44bd-995e-aef9fe784b8a.jpg',
            type: 'image'
          },
          {
            url: P + 'fbd17779-ff8d-4ce0-a3e2-78a9debbe460.jpg',
            type: 'image'
          },
          {
            url: P + 'b6e8feab-70cc-421c-a94c-adf1deb96abc.jpg',
            type: 'image'
          },
          { url: P + 'e85f53c9-4a1f-436f-b4d5-caa35cfe9ace.jpg', type: 'image' }
        ]
      },
      {
        code: 'AG09',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '1b7bdc6a-3cf1-4235-b444-79c5e1409dcd.jpg',
            type: 'image'
          },
          {
            url: P + '8fa1452d-2d16-476e-a9f4-8f410270b5f4.jpg',
            type: 'image'
          },
          {
            url: P + '784dcc03-9e5e-43f6-826f-65ceb73e9e40.jpg',
            type: 'image'
          },
          {
            url: P + '54feda9d-964c-4e06-aa58-0f0fa4880f4d.jpg',
            type: 'image'
          },
          { url: P + '7687e362-448a-498f-a4f8-650b539f75d8.jpg', type: 'image' }
        ]
      },
      {
        code: 'TC22',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '918309e1-100f-4f79-95d7-0d52a782de40.jpg',
            type: 'image'
          },
          {
            url: P + 'a41fda8b-7793-479e-a3af-638f55641d32.jpg',
            type: 'image'
          },
          {
            url: P + 'dc97f93f-e4d4-488e-a617-c6b75610c182.jpg',
            type: 'image'
          },
          {
            url: P + '96195fe1-34e9-40b2-95c4-ec89406c7e75.jpg',
            type: 'image'
          },
          { url: P + '13f5d88c-d515-43da-a679-a4a24c822fdc.jpg', type: 'image' }
        ]
      }
    ]
  },
  {
    id: 'main-shaft-lightning',
    name: '4. 整机主轴防雷',
    standard:
      '工艺标准：间隙为5-10mm，要求水印有4张图像（左上、左下、右上、右下），描述具体间隙值',
    items: [
      {
        code: 'TC20',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + '8f1736ca-624c-42e6-9f47-7a0876b3dd15.jpg',
            type: 'image'
          },
          {
            url: P + '2341450f-c8a3-4ba9-b154-3aed58dad6ef.jpg',
            type: 'image'
          },
          {
            url: P + '02dc18c8-ab59-4c42-a915-6ab6b87543a5.jpg',
            type: 'image'
          },
          { url: P + '8d146757-21cd-435a-9aa8-7372a93d8da5.jpg', type: 'image' }
        ]
      },
      {
        code: 'NX01',
        label: '正样本，通过',
        tag: '正样本',
        media: [
          {
            url: P + 'd1b7d6bc-3534-4d14-99b3-d48dc3e7d701.jpg',
            type: 'image'
          },
          {
            url: P + 'e5dff55d-478b-4672-9bbd-74fb276ae734.jpg',
            type: 'image'
          },
          {
            url: P + 'f8b208dd-c3a2-45ab-a78d-f7e7fd504612.jpg',
            type: 'image'
          },
          { url: P + 'f0c283ad-5322-4110-90bf-ea55d470e7ed.jpg', type: 'image' }
        ]
      },
      {
        code: 'LG01',
        label: '负样本',
        tag: '负样本',
        desc: '缺少两张图像',
        media: [
          {
            url: P + '068ef59e-8a34-4de5-890e-909e38cd4129.jpg',
            type: 'image'
          },
          { url: P + '63b24631-5a0e-4c3c-a3a3-72bf2ec0b162.jpg', type: 'image' }
        ]
      },
      {
        code: 'AG08',
        label: '负样本',
        tag: '负样本',
        desc: '缺少两张图像，上传了无水印图像（无水印图像无法分类，直接跳过）',
        media: [
          {
            url: P + '99c04691-255c-46eb-8dbb-dc3dccd4821e.jpg',
            type: 'image'
          },
          {
            url: P + '0224a084-211e-4e2e-88d9-6e2e383835a6.jpg',
            type: 'image'
          },
          {
            url: P + '951aeece-8f0b-4db0-9987-26064fc9a731.jpg',
            type: 'image'
          },
          { url: P + '08bb31f3-3124-483b-a539-3fc76bf113a4.jpg', type: 'image' }
        ],
        rectifyMedia: [
          {
            url: P + '8762abda-510a-4370-a8be-c4ca5ad46a27.jpg',
            type: 'image'
          },
          { url: P + 'e2fd00f2-2058-477a-9b39-6cb3095483e2.jpg', type: 'image' }
        ]
      },
      {
        code: 'AT07',
        label: '负样本',
        tag: '负样本',
        desc: '尺寸不满足工艺标准',
        media: [
          {
            url: P + '4799b7be-cba2-4f0c-a1d6-f918548c569b.jpg',
            type: 'image'
          },
          {
            url: P + '1f059931-b09b-45c1-a277-4f88d4789cb4.jpg',
            type: 'image'
          },
          {
            url: P + 'c34bfc80-852f-499b-988d-641b34fd72a3.jpg',
            type: 'image'
          },
          { url: P + '4c2e7857-5da9-4df5-a9c8-82e7ad6822d5.jpg', type: 'image' }
        ],
        rectifyMedia: [
          {
            url: P + 'be82ed42-7726-483d-81e8-276a708468e8.jpg',
            type: 'image'
          },
          {
            url: P + 'd6cf0a75-11e3-46de-b2d8-dd2eaf3f11e5.jpg',
            type: 'image'
          },
          {
            url: P + '35c205b5-2195-4c88-8a32-5f86267a42bd.jpg',
            type: 'image'
          },
          { url: P + '25f888f4-13d1-4d15-a20d-f1b95cfe68fa.jpg', type: 'image' }
        ]
      }
    ]
  }
]

// ============================================================
// 上传阶段
// ============================================================

type Phase =
  | 'idle' // 等待选择
  | 'previewing' // 预览文件
  | 'uploading' // 分析中
  | 'success' // 发送成功
  | 'error' // 发送失败

// ============================================================
// Props
// ============================================================

const SimulatePage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const groupId = useAppSelector((s: any) => s.group.groupId || '')
  const userId = useAppSelector((s: any) => s.main.user?.user_id || '')
  // -- 手机模拟器状态 --
  const [phase, setPhase] = useState<Phase>('idle')
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [mediaType, setMediaType] = useState<number>(0)
  const [simulateResult, setSimulateResult] = useState<SimulateResult | null>(
    null
  )
  const [statusText, setStatusText] = useState<string>('点击选择点位照片/视频')
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  const [showSyncBar, setShowSyncBar] = useState(false)
  const [showComplete, setShowComplete] = useState(false)
  const syncTimerRef = useRef<any>(null)

  // -- 追踪面板状态 --
  const [trackerList, setTrackerList] = useState<TrackerCheckpoint[]>([])
  const [trackerLoading, setTrackerLoading] = useState(false)
  const pollingRef = useRef<any>(null)

  // ============================================================
  // 追踪面板轮询
  // ============================================================

  const fetchTracker = useCallback(async () => {
    if (!groupId) return
    try {
      setTrackerLoading(true)
      const data = await apiGetActiveCheckpoints(dispatch, {
        group_id: groupId
      })
      setTrackerList((data?.checkpoints || []) as TrackerCheckpoint[])
    } catch (e) {
      console.warn('[Tracker] 轮询失败:', e)
    } finally {
      setTrackerLoading(false)
    }
  }, [dispatch, groupId])

  const burstRef = useRef<any>(null)

  // 常规轮询（20s）
  const startPolling = useCallback(() => {
    if (pollingRef.current) return
    fetchTracker()
    pollingRef.current = setInterval(fetchTracker, 20000)
  }, [fetchTracker])

  // 密集刷新：立即刷新一次，然后 2s 间隔连续刷新 5 次，之后恢复常规轮询
  const burstRefresh = useCallback(() => {
    // 清理上一轮 burst
    if (burstRef.current) clearInterval(burstRef.current)

    fetchTracker() // 立即刷一次
    let count = 0
    burstRef.current = setInterval(() => {
      count++
      fetchTracker()
      if (count >= 5) {
        clearInterval(burstRef.current)
        burstRef.current = null
      }
    }, 2000)
  }, [fetchTracker])

  // 进入页面立即开启轮询
  useEffect(() => {
    startPolling()
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
      if (burstRef.current) {
        clearInterval(burstRef.current)
        burstRef.current = null
      }
    }
  }, [startPolling])

  // ============================================================
  // 照片选择
  // ============================================================

  const handleSelectMedia = (url: string, type: 'image' | 'video') => {
    setPreviewUrl(url)
    setMediaType(type === 'video' ? 1 : 0)
    setSimulateResult(null)
    setShowPhotoModal(false)
    // 显示同步条 4 秒
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
    setShowSyncBar(true)
    syncTimerRef.current = setTimeout(() => setShowSyncBar(false), 4000)
    // 自动进入 uploading，跳过预览
    setPhase('uploading')
    setStatusText('Thinking...')
    autoSend(url, type === 'video' ? 1 : 0)
  }

  const handleClearPreview = () => {
    setPreviewUrl('')
    setPhase('idle')
    setStatusText('点击选择点位照片/视频')
    setSimulateResult(null)
  }

  // ============================================================
  // 自动发送（选择后立即调用，不需要手动点击发送）
  // ============================================================

  const autoSend = async (fileUrl: string, fileMediaType: number) => {
    if (!fileUrl || !groupId) return
    try {
      const result = await apiSimulatePhoto(dispatch, {
        group_id: groupId,
        file_url: fileUrl,
        media_type: fileMediaType,
        user_id: userId
      })
      setSimulateResult(result)
      startPolling()
      burstRefresh()

      const cp = result?.checkpoint
      const allDone =
        cp &&
        cp.image_count >= cp.required_images &&
        cp.video_count >= cp.required_videos
      if (allDone) {
        setPhase('success')
        setStatusText('素材收集完成，已提交分析')
        // 等同步进度条动画结束（4秒）后显示"采集完成"2秒，再切回默认
        setTimeout(() => {
          setShowComplete(true)
          setTimeout(() => {
            setShowComplete(false)
            setPreviewUrl('')
            setSimulateResult(null)
            setPhase('idle')
            setStatusText('点击选择点位照片/视频')
          }, 2000)
        }, 4000)
      } else {
        setPhase('success')
        setStatusText('发送成功，请继续选择')
      }
    } catch (e) {
      setPhase('error')
      setStatusText(`发送失败: ${(e as Error).message || '未知错误'}`)
    }
  }

  const handleRetake = () => {
    handleClearPreview()
    setShowPhotoModal(true)
  }

  // ============================================================
  // 渲染 Pipeline（节点行 + 标签行）
  // ============================================================

  const renderPipeline = (tracker: TrackerCheckpoint) => {
    const nodes = computeNodeStates(tracker)
    return (
      <div className="tracker-card__pipeline">
        {/* 节点 + 连线行 */}
        <div className="pipeline">
          {nodes.map((node, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && (
                <div
                  className={`pipeline-line pipeline-line--${getLineState(
                    nodes,
                    idx - 1
                  )}`}
                />
              )}
              <div className={`pipeline-node pipeline-node--${node.state}`}>
                {node.state === 'complete' && <CheckOutlined />}
                {node.state === 'fail' && <CloseOutlined />}
              </div>
            </React.Fragment>
          ))}
        </div>
        {/* 标签行 */}
        <div className="pipeline-labels">
          {nodes.map((node, idx) => (
            <React.Fragment key={idx}>
              <div className="pipeline-label-node">
                <span
                  className={`pipeline-label-text pipeline-label-text--${node.state}`}
                >
                  {node.label}
                </span>
              </div>
              {idx < nodes.length - 1 && (
                <div className="pipeline-label-spacer" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    )
  }

  // ============================================================
  // 渲染 Tracker 阶段详情
  // ============================================================

  const renderTrackerDetail = (tracker: TrackerCheckpoint) => {
    const { stage } = tracker

    if (stage === 'collecting') {
      const imgReq = tracker.required_images
      const vidReq = tracker.required_videos
      const imgKnown = imgReq != null
      const vidKnown = vidReq != null
      const imgDone = imgKnown && imgReq > 0 && tracker.current_images >= imgReq
      const vidDone = vidKnown && vidReq > 0 && tracker.current_videos >= vidReq
      const imgPct =
        imgKnown && imgReq > 0
          ? Math.min(100, Math.round((tracker.current_images / imgReq) * 100))
          : imgKnown && imgReq === 0
            ? 100
            : 0
      const vidPct =
        vidKnown && vidReq > 0
          ? Math.min(100, Math.round((tracker.current_videos / vidReq) * 100))
          : vidKnown && vidReq === 0
            ? 100
            : 0

      return (
        <div className="tracker-detail tracker-detail--collecting">
          <div className="collect-row">
            <div className="collect-row__label">
              <PictureOutlined />
              <span>图片</span>
            </div>
            <div className="collect-row__bar">
              <div className="collect-bar">
                <div
                  className={`collect-bar__fill${
                    imgDone ? ' collect-bar__fill--done' : ''
                  }`}
                  style={{ width: imgKnown ? `${imgPct}%` : '0%' }}
                />
              </div>
            </div>
            <div
              className={`collect-row__count${
                imgDone ? ' collect-row__count--done' : ''
              }`}
            >
              {tracker.current_images}/{imgKnown ? imgReq : '?'}
              {imgDone && <CheckCircleFilled />}
            </div>
          </div>
          <div className="collect-row">
            <div className="collect-row__label">
              <VideoCameraOutlined />
              <span>视频</span>
            </div>
            <div className="collect-row__bar">
              <div className="collect-bar">
                <div
                  className={`collect-bar__fill${
                    vidDone ? ' collect-bar__fill--done' : ''
                  }`}
                  style={{ width: vidKnown ? `${vidPct}%` : '0%' }}
                />
              </div>
            </div>
            <div
              className={`collect-row__count${
                vidDone ? ' collect-row__count--done' : ''
              }`}
            >
              {tracker.current_videos}/{vidKnown ? vidReq : '?'}
              {vidDone && <CheckCircleFilled />}
            </div>
          </div>
        </div>
      )
    }
    if (stage === 'analyzing') {
      return (
        <div className="tracker-detail tracker-detail--analyzing">
          <span className="analyzing-spinner" />
          <span>AI 大模型分析中...</span>
        </div>
      )
    }
    if (stage === 'compliant') {
      return (
        <div className="tracker-detail tracker-detail--pass">
          <CheckCircleFilled />
          <span>检测通过</span>
        </div>
      )
    }
    if (stage === 'non_compliant') {
      return (
        <div className="tracker-detail tracker-detail--fail">
          <CloseCircleFilled />
          <span>不符合{tracker.reason ? `：${tracker.reason}` : ''}</span>
        </div>
      )
    }
    if (stage === 'approved') {
      return (
        <div className="tracker-detail tracker-detail--pass">
          <CheckCircleFilled />
          <span>工单处理完成</span>
        </div>
      )
    }
    return (
      <div className="tracker-detail">
        {tracker.reason && (
          <div
            className="tracker-detail tracker-detail--fail"
            style={{ marginBottom: 4 }}
          >
            <CloseCircleFilled />
            <span>{tracker.reason}</span>
          </div>
        )}
        {stage && (
          <Tag className="tracker-detail__stage-tag">
            {STAGE_LABELS[stage] || stage}
          </Tag>
        )}
      </div>
    )
  }

  // ============================================================
  // 渲染 Tracker 卡片
  // ============================================================

  const renderTrackerCard = (tracker: TrackerCheckpoint) => {
    const showAction =
      !!tracker.work_order_id &&
      ['rectifying', 'rejected', 'non_compliant'].includes(tracker.stage)

    return (
      <div className="tracker-card" key={tracker.checkpoint_key}>
        <div className="tracker-card__header">
          <span className="tracker-card__name">{tracker.checkpoint_name}</span>
          {tracker.machine_id && (
            <span className="tracker-card__tag">{tracker.machine_id}</span>
          )}
          {tracker.site_name && (
            <span className="tracker-card__tag">{tracker.site_name}</span>
          )}
          {tracker.last_activity && (
            <span className="tracker-card__time">
              {shortTime(tracker.last_activity)}
            </span>
          )}
        </div>

        {renderPipeline(tracker)}
        {renderTrackerDetail(tracker)}

        {showAction && (
          <div className="tracker-card__actions">
            <button
              className="tracker-action-btn"
              type="button"
              onClick={() =>
                navigate(
                  `/myse/orders?checkpoint_key=${encodeURIComponent(
                    tracker.checkpoint_key
                  )}`
                )
              }
            >
              <FormOutlined />
              <span>去整改</span>
            </button>
          </div>
        )}
      </div>
    )
  }

  // ============================================================
  // 主渲染
  // ============================================================

  return (
    <div className="simulate-page">
      <div className="simulate-page__content">
        <div className="simulate-page__header">
          <h2>检查项配置</h2>
        </div>

        <div className="simulate-layout">
          {/* ========== 左侧：手机模拟器 ========== */}
          <div className="phone-frame">
            <div className="phone-frame__notch" />
            <div className="phone-screen">
              {/* 预览区 */}
              <div
                className="phone-camera-area"
                style={
                  previewUrl
                    ? { background: '#000' }
                    : {
                        backgroundImage: `url(${defaultBgImg})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'right bottom'
                      }
                }
              >
                <div className="phone-preview">
                  {previewUrl && mediaType === 0 && (
                    <img src={previewUrl} alt="preview" />
                  )}
                  {previewUrl && mediaType === 1 && (
                    <video src={previewUrl} muted />
                  )}
                  {showComplete && (
                    <div className="phone-complete-overlay">
                      <div className="phone-complete-overlay__icon">
                        <CheckCircleFilled />
                      </div>
                      <span>采集完成</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 底部浮层 — 绝对定位在屏幕底部，独立图层 */}
              <div className="phone-bottom-overlay">
                <div className="phone-bottom-overlay__top">
                  <div className="phone-home__status">
                    <span>08:08</span>
                    <span className="status-battery" />
                  </div>
                  <div className="phone-home__nav">
                    <MenuOutlined />
                    <ThunderboltOutlined />
                    <span className="nav-entry">照片验真</span>
                    <span className="nav-vip">VIP</span>
                    <ScanOutlined />
                  </div>
                </div>

                <div className="phone-bottom-overlay__bottom">
                  <div className="phone-sync-slot">
                    {showSyncBar ? (
                      <div className="phone-sync-bar phone-sync-bar--active">
                        <CloudUploadOutlined />
                        <span>正在同步到：明阳能源项目</span>
                      </div>
                    ) : (
                      <img
                        className="phone-sync-done-img"
                        src={syncDoneImg}
                        alt="sync-done"
                      />
                    )}
                  </div>
                  <div className="phone-action-area">
                    <div className="photo-thumb">
                      <img src={DEFAULT_HOME_PREVIEW} alt="thumb" />
                    </div>
                    {(phase === 'idle' || phase === 'success') && (
                      <button
                        className="capture-btn"
                        type="button"
                        onClick={() => setShowPhotoModal(true)}
                      >
                        <span className="capture-btn__inner" />
                      </button>
                    )}

                    {phase === 'uploading' && (
                      <Button disabled>
                        <RobotOutlined style={{ marginRight: 4 }} />
                        分析中
                      </Button>
                    )}

                    {phase === 'error' && (
                      <Button type="primary" onClick={handleRetake}>
                        继续选择
                      </Button>
                    )}
                    <IdcardOutlined className="watermark-icon" />
                  </div>

                  <div className="phone-mode-tabs">
                    <span>照片编辑</span>
                    <span>视频</span>
                    <span className="active">照片</span>
                    <span>边拍边拼</span>
                    <span>拼图汇报</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="phone-frame__home" />
          </div>

          {/* ========== 右侧：追踪面板 ========== */}
        </div>
      </div>

      {/* ========== 照片选择弹窗 ========== */}
      <Modal
        title="选择点位照片/视频"
        open={showPhotoModal}
        onCancel={() => setShowPhotoModal(false)}
        footer={null}
        width={960}
        className="photo-select-modal"
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
            padding: '16px 24px'
          }
        }}
      >
        {SAMPLE_DATA.map((category) => (
          <div className="sample-category" key={category.id}>
            <div className="sample-category__header">
              <h3>{category.name}</h3>
              {category.standard && (
                <p className="sample-category__standard">{category.standard}</p>
              )}
            </div>
            <div className="sample-category__list">
              {category.items.map((item) => (
                <div className="sample-item" key={item.code}>
                  <div className="sample-item__header">
                    <span className="sample-item__code">{item.code}</span>
                    <Tag color={item.tag === '正样本' ? 'green' : 'red'}>
                      {item.label}
                    </Tag>
                  </div>
                  {item.desc && (
                    <p className="sample-item__desc">{item.desc}</p>
                  )}
                  <div className="media-grid">
                    {item.media.map((m, idx) => (
                      <div
                        className={`media-thumb${
                          m.type === 'video' ? ' media-thumb--video' : ''
                        }`}
                        key={idx}
                        onClick={() => handleSelectMedia(m.url, m.type)}
                        title={
                          m.desc ||
                          `${item.code} - ${
                            m.type === 'image' ? '图片' : '视频'
                          } ${idx + 1}`
                        }
                      >
                        {m.type === 'image' ? (
                          <img src={m.url} alt={`${item.code}-${idx + 1}`} />
                        ) : (
                          <div className="media-thumb__video">
                            <video src={m.url} preload="metadata" muted />
                            <div className="media-thumb__play">
                              <PlayCircleOutlined />
                            </div>
                          </div>
                        )}
                        {m.desc && (
                          <div className="media-thumb__desc">{m.desc}</div>
                        )}
                      </div>
                    ))}
                  </div>
                  {item.rectifyMedia && item.rectifyMedia.length > 0 && (
                    <div className="sample-item__rectify">
                      <div className="sample-item__rectify-label">
                        <RedoOutlined />
                        <span>整改后</span>
                      </div>
                      <div className="media-grid">
                        {item.rectifyMedia.map((m, idx) => (
                          <div
                            className={`media-thumb${
                              m.type === 'video' ? ' media-thumb--video' : ''
                            }`}
                            key={`r-${idx}`}
                            onClick={() => handleSelectMedia(m.url, m.type)}
                            title={`${item.code} 整改后 - ${
                              m.type === 'image' ? '图片' : '视频'
                            } ${idx + 1}`}
                          >
                            {m.type === 'image' ? (
                              <img
                                src={m.url}
                                alt={`${item.code}-rectify-${idx + 1}`}
                              />
                            ) : (
                              <div className="media-thumb__video">
                                <video src={m.url} preload="metadata" muted />
                                <div className="media-thumb__play">
                                  <PlayCircleOutlined />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </Modal>
    </div>
  )
}

// ============================================================
// Redux 连接
export default SimulatePage
